import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ReferenceDataCategoriesConstants } from '../../../../../lib/constants/reference-data-categories';
import { ReferenceDataService } from '../../../../../lib/services/reference-data.service';
import { BaseApplicantComponent } from '../base-applicant.component';
import { MortgageApplicationStore } from './../../../services/mortgage-application-store';
import { ReferenceDataConstants } from '../../../../../lib/constants/reference-data-constants';
import { FormTextInputComponent } from '../../../../shared/components/form-inputs/form-text-input/form-text-input.component';

@Component({
  selector: 'boi-applicant-details',
  templateUrl: './applicant-details.component.html',
  styleUrls: ['./applicant-details.component.scss']
})
export class ApplicantDetailsComponent extends BaseApplicantComponent implements AfterViewInit, OnChanges {
  public customerTypeOptionList: string;
  public referenceDataConstants = ReferenceDataConstants;
  public informationBoxMessage: string;
  public maxLengthPhoneNumber: number;

  @ViewChild('applicantEmailAddress') applicantEmailAddressElement: FormTextInputComponent;
  @ViewChild('applicantMobilePhoneNumber') applicantMobilePhoneNumberElement: FormTextInputComponent;
  @ViewChild('applicantHomePhoneNumber') applicantHomePhoneNumberElement: FormTextInputComponent;
  @ViewChild('applicantWorkPhoneNumber') applicantWorkPhoneNumberElement: FormTextInputComponent;
  @ViewChild('applicantEmailAddress', { read: ElementRef }) applicantEmailAddressElementRef: ElementRef;
  @ViewChild('applicantMobilePhoneNumber', { read: ElementRef })
  applicantMobilePhoneNumberElementRef: ElementRef;
  @ViewChild('applicantHomePhoneNumber', { read: ElementRef })
  applicantHomePhoneNumberElementRef: ElementRef;
  @ViewChild('applicantWorkPhoneNumber', { read: ElementRef })
  applicantWorkPhoneNumberElementRef: ElementRef;

  constructor(
    protected mortgageApplicationStore: MortgageApplicationStore,
    private readonly referenceDataService: ReferenceDataService,
    private readonly changeDetectionRef: ChangeDetectorRef
  ) {
    super(mortgageApplicationStore);

    this.maxLengthPhoneNumber = parseInt(
      this.referenceDataService.getConfigurationValue(
        ReferenceDataCategoriesConstants.CLIENT_ENVIRONMENT_CATEGORY,
        ReferenceDataConstants.ContactNumberLength
      )
    );
  }

  public ngAfterViewInit(): void {
    this.setCustomerTypeOptionList();

    if (this.mortgageApplication.isBuyToLet && this.mortgageApplication.isPurchase) {
      this.applicant.CustomerTypeCode = this.referenceDataLabels.CustomerTypePurchase;
    }

    if (
      !this.mortgageApplication.IsFactFind &&
      !this.mortgageApplication.IsAip &&
      !this.applicant.OfferDistributionChannel
    ) {
      this.onOfferDistributionChange(this.referenceDataOptionLists.OfferDistributionOnline, this.applicantIndex);
    } else {
      this.updateInfoBoxes();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const isDataInitialized =
      this.mortgageApplication && this.applicant && this.applicant !== changes.applicant.previousValue;

    if (
      isDataInitialized &&
      this.mortgageApplication.isRemortgage &&
      this.applicant !== changes.applicant.previousValue
    ) {
      this.setCustomerTypeIfRemortgage();
    }

    if (isDataInitialized && this.mortgageApplication.isBuyToLet && this.mortgageApplication.isPurchase) {
      this.applicant.CustomerTypeCode = this.referenceDataLabels.CustomerTypePurchase;
    }

    if (isDataInitialized && this.applicant !== changes.applicant.previousValue) {
      this.updateAgeAtMortgageTerm();
      this.updateInfoBoxes();
    }

    if (changes?.applicantIndex) {
      setTimeout(() => {
        this.setErrorIfNotNullish(this.applicantMobilePhoneNumberElement, false);
        this.setErrorIfNotNullish(this.applicantWorkPhoneNumberElement, false);
        this.setErrorIfNotNullish(this.applicantHomePhoneNumberElement, false);
        this.setErrorIfNotNullish(this.applicantMobilePhoneNumberElement, false);
        this.setErrorIfNotNullish(this.applicantEmailAddressElement, false);

        this.changeDetectionRef.detectChanges();
      }, 0);
    }
  }

  private setErrorIfNotNullish(elem: object, valueToBeSet: any): void {
    const errorProp: string = 'hasError';
    this.setIfNotNullish(elem, errorProp, valueToBeSet);
  }

  private setIfNotNullish(elem: object, prop: string, valueToBeSet: any): void {
    if (elem) {
      elem[prop] = valueToBeSet;
    }
  }

  public initialize(): void {
    this.updateAgeAtMortgageTerm();
  }

  onOfferDistributionChange(newValue: string, applicantIndex: number): void {
    this.mortgageApplication.Applicants[applicantIndex].OfferDistributionChannel = newValue;
    this.mortgageApplication.OfferDistributionChannel = newValue;

    if (applicantIndex === 0) {
      for (let i = 0; i < this.mortgageApplication.Applicants.length; i++) {
        this.mortgageApplication.Applicants[i].OfferDistributionChannel = newValue;
      }
    }

    this.updateInfoBoxes();
    this.handleOfferDistributionChangeValidations(newValue);
  }

  public updateAgeAtMortgageTerm(): void {
    if (
      this.mortgageApplication.IsFactFind &&
      this.mortgageApplication.Applicants.length > this.applicantIndex &&
      (this.mortgageApplication.isResidential || this.isLoanGroupTypeBtlAffordability())
    ) {
      const currentApplicant = this.mortgageApplication.Applicants[this.applicantIndex];
      if (currentApplicant.DateOfBirth) {
        const applicantAgeInMonths = this.calculateMonthsTillNow(currentApplicant.DateOfBirth);
        const loanTermInMonths = this.calculateLoanTermInMonths();
        currentApplicant.AgeAtMortgageTerm = Math.floor((applicantAgeInMonths + loanTermInMonths) / 12);
      }
    }
  }

  private calculateMonthsTillNow(date: Date): number {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const startDate = new Date(date);

    return (currentYear - startDate.getFullYear()) * 12 + currentMonth - startDate.getMonth();
  }

  private calculateLoanTermInMonths(): number {
    const caseProduct = this.mortgageApplication.ApplicationProducts[0].CaseProduct;

    return caseProduct ? caseProduct.LoanTermYears * 12 + caseProduct.LoanTermMonths : 0;
  }

  private setCustomerTypeOptionList(): void {
    this.customerTypeOptionList =
      this.applicant.IsPartToRemortgageCode === this.referenceDataOptionLists.AnswerTypeYes
        ? this.referenceDataOptionLists.CustomerTypeRemortgageOnly
        : this.getCustomerTypeCodesIfNotRemortgage();
  }

  private setCustomerTypeCodeIfRadioButtonChange(): void {
    this.applicant.CustomerTypeCode = this.applicant.isPartToRemortgage()
      ? this.referenceDataLabels.CustomerTypeRemortgaging
      : this.mortgageApplication.isBuyToLet &&
        this.applicant.IsPartToRemortgageCode === this.referenceDataOptionLists.AnswerTypeNo
      ? this.referenceDataLabels.CustomerTypePurchase
      : this.applicant.CustomerTypeCode === this.referenceDataOptionLists.CustomerTypeRemortgaging
      ? undefined
      : this.applicant.CustomerTypeCode;
  }

  public setCustomerTypeIfRemortgage(): void {
    this.setCustomerTypeOptionList();
    this.setCustomerTypeCodeIfRadioButtonChange();
  }

  public updateApplicantMortgagePayOffDependencies(): void {
    if (this.mortgageApplication.isBuyToLet && this.applicant.isPartToRemortgage()) {
      this.applicant.ResidentialMortgageOwed = this.mortgageApplication.MortgagePayOffAmount;
      this.applicant.HoldMortgageBTLCode =
        this.mortgageApplication.MortgagePayOffAmount > 0
          ? this.referenceDataOptionLists.AnswerTypeYes
          : this.referenceDataOptionLists.AnswerTypeNo;
    }
  }

  private getCustomerTypeCodesIfNotRemortgage(): string {
    let optionList: string;
    if (this.mortgageApplication.isPurchase) {
      optionList = this.mortgageApplication.isResidential
        ? this.referenceDataOptionLists.CustomerTypePurchaseResidential
        : this.referenceDataOptionLists.CustomerTypePurchaseBTL;
    } else if (this.mortgageApplication.isRemortgage) {
      optionList = this.mortgageApplication.isResidential
        ? this.referenceDataOptionLists.CustomerTypeRemortgageResidential
        : this.referenceDataOptionLists.CustomerTypeRemortgageBTL;
    }

    return optionList;
  }

  public onSponsorCodeUpdate(): void {
    if (this.applicant.IsLoanSponsorCode === this.referenceDataOptionLists.AnswerTypeYes) {
      for (const applicant of this.mortgageApplication.Applicants) {
        if (this.applicant !== applicant) {
          applicant.IsLoanSponsorCode = this.referenceDataOptionLists.AnswerTypeNo;
        }
      }
    }
  }

  public isApplicantSponsorFieldVisible(): boolean {
    const firstStart = this.referenceDataService.getConfigurationValue(
      ReferenceDataCategoriesConstants.CLIENT_ENVIRONMENT_CATEGORY,
      ReferenceDataConstants.ClientEnvironmentFirstStart
    );

    if (
      !this.mortgageApplication.isFactFindFirstStartEnabled(firstStart) &&
      !this.isResidentialLoanGroupTypeFirstStart()
    ) {
      return false;
    }

    if (this.isApplicantSponsorSelected() && !this.applicant.IsLoanSponsorCode) {
      this.applicant.IsLoanSponsorCode = this.referenceDataOptionLists.AnswerTypeNo;
    }

    return (
      this.applicant.IsLoanSponsorCode === this.referenceDataOptionLists.AnswerTypeYes ||
      !this.isApplicantSponsorSelected()
    );
  }

  private isApplicantSponsorSelected(): boolean {
    for (const applicant of this.mortgageApplication.Applicants) {
      if (applicant.IsLoanSponsorCode === this.referenceDataOptionLists.AnswerTypeYes) {
        return true;
      }
    }

    return false;
  }

  public shouldDisplaySolicitorHeader(): boolean {
    const isLoanSponsorAndNotOnDeeds =
      this.applicant.IsLoanSponsorCode === this.referenceDataOptionLists.AnswerTypeYes &&
      this.applicant.IsOnDeedsCode === this.referenceDataOptionLists.AnswerTypeNo;
    const isFamilyLinkAndRemortgaging =
      this.isLoanGroupTypeFamilyLink() &&
      this.applicant.CustomerTypeCode === this.referenceDataLabels.CustomerTypeRemortgaging;

    return isLoanSponsorAndNotOnDeeds || isFamilyLinkAndRemortgaging || this.isLoanGroupTypeRetirementLink();
  }

  public updateInfoBoxes(): void {
    const mainApplicant = this.mortgageApplication.Applicants[0];
    const selectedOption = mainApplicant.OfferDistributionChannel;
    const isNotMainApplicant = this.applicantIndex !== 0;

    switch (selectedOption) {
      case this.referenceDataOptionLists.OfferDistributionOnline:
        if (isNotMainApplicant) {
          this.informationBoxMessage = this.referenceDataLabels.ProvideEmailAndPhoneNumber;
        } else {
          this.informationBoxMessage = this.referenceDataLabels.ProvideTelephoneNumber;
        }

        break;

      case this.referenceDataOptionLists.OfferDistributionByPost:
        this.informationBoxMessage = this.referenceDataLabels.ProvideAtLeastOnePhoneNumber;

        break;
    }
  }

  private handleOfferDistributionChangeValidations(offerDistribution: string): void {
    if (offerDistribution == this.referenceDataOptionLists.OfferDistributionOnline) {
      this.applicantMobilePhoneNumberElement.hasError = false;
      this.applicantWorkPhoneNumberElement.hasError = false;
      this.applicantHomePhoneNumberElement.hasError = false;
      this.applicantEmailAddressElementRef.nativeElement.children[0].children[0].children[1].children[0].children[0].style.fontWeight =
        'bold';
      this.applicantMobilePhoneNumberElementRef.nativeElement.children[0].children[0].children[1].children[0].children[0].style.fontWeight =
        'bold';
      this.applicantHomePhoneNumberElementRef.nativeElement.children[0].children[0].children[1].children[0].children[0].style.fontWeight =
        'normal';
      this.applicantWorkPhoneNumberElementRef.nativeElement.children[0].children[0].children[1].children[0].children[0].style.fontWeight =
        'normal';
    }

    if (offerDistribution == this.referenceDataOptionLists.OfferDistributionByPost) {
      this.applicantMobilePhoneNumberElement.hasError = false;
      this.applicantEmailAddressElement.hasError = false;
      this.applicantEmailAddressElementRef.nativeElement.children[0].children[0].children[1].children[0].children[0].style.fontWeight =
        'normal';
      this.applicantMobilePhoneNumberElementRef.nativeElement.children[0].children[0].children[1].children[0].children[0].style.fontWeight =
        'bold';
      this.applicantHomePhoneNumberElementRef.nativeElement.children[0].children[0].children[1].children[0].children[0].style.fontWeight =
        'bold';
      this.applicantWorkPhoneNumberElementRef.nativeElement.children[0].children[0].children[1].children[0].children[0].style.fontWeight =
        'bold';
    }
  }
}
